import type { IconProps } from '@iconify/react';
import type { ComponentProps } from 'react';

import { Icon } from '@iconify/react';
import { styled } from '@mui/material/styles';
import { useId } from 'react';

import { iconifyClasses } from './classes';
import { allIconNames, registerIcons, type IconifyName } from './register-icons';

export type IconifyProps = ComponentProps<typeof IconRoot> &
  Omit<IconProps, 'icon'> & {
    icon: IconifyName;
  };

// Typed wrapper around `@iconify/react`'s `<Icon>`. Only names registered in
// `icon-sets.ts` are accepted at compile time — passing an unregistered name
// falls back to a network fetch and logs a console warning so we know to add
// the icon to the offline set.
export function Iconify({ className, icon, width = 20, height, sx, ...other }: IconifyProps) {
  const uniqueId = useId();

  if (!allIconNames.includes(icon)) {
    console.warn(
      [
        `Icon "${icon}" is currently loaded online, which may cause flickering effects.`,
        `To ensure a smoother experience, please register your icon in shared/components/iconify/icon-sets.ts.`,
      ].join('\n'),
    );
  }

  registerIcons();

  return (
    <IconRoot
      ssr
      id={uniqueId}
      icon={icon}
      className={[iconifyClasses.root, className].filter(Boolean).join(' ')}
      sx={[
        {
          width,
          flexShrink: 0,
          height: height ?? width,
          display: 'inline-flex',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...other}
    />
  );
}

const IconRoot = styled(Icon)``;
